import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Trophy, Clock, CheckCircle2, X, Sparkles } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtCT = (iso) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        }) + ' CT';
    } catch { return ''; }
};

/**
 * Series Pack — predict an entire playoff series.
 *
 * Player picks:
 *   1. How many games they think the series ends in (min_games..max_games)
 *   2. The winner of each predicted game
 *
 * Tier payouts on # of correct picks. JACKPOT for exact length + all correct.
 */
const SeriesPackBanner = () => {
    const [pack, setPack] = useState(null);
    const [myEntry, setMyEntry] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [countdown, setCountdown] = useState('');

    // Picker state
    const [predictedLength, setPredictedLength] = useState(0);
    const [picks, setPicks] = useState([]);

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/series-pack/active`);
            setPack(data?.pack || null);
            setMyEntry(data?.my_entry || null);
        } catch {
            setPack(null);
            setMyEntry(null);
        }
    }, []);

    useEffect(() => {
        load();
        const id = setInterval(load, 60_000);
        return () => clearInterval(id);
    }, [load]);

    // Countdown to first kickoff
    useEffect(() => {
        if (!pack?.games?.length) { setCountdown(''); return undefined; }
        const earliest = pack.games
            .map(g => g.commence_time)
            .filter(Boolean)
            .sort()[0];
        if (!earliest) { setCountdown(''); return undefined; }
        const tick = () => {
            const ms = new Date(earliest).getTime() - Date.now();
            if (ms <= 0) { setCountdown('Entries closed — Game 1 has started'); return; }
            const mins = Math.floor(ms / 60_000);
            if (mins < 60) { setCountdown(`Locks in ${mins}m`); return; }
            const hrs = Math.floor(mins / 60);
            const m = mins % 60;
            if (hrs < 24) { setCountdown(`Locks in ${hrs}h ${m}m`); return; }
            setCountdown(`Locks in ${Math.floor(hrs / 24)}d`);
        };
        tick();
        const id = setInterval(tick, 30_000);
        return () => clearInterval(id);
    }, [pack]);

    // Reset picker when dialog opens
    useEffect(() => {
        if (openDialog && pack) {
            const minG = Number(pack.min_games || 4);
            setPredictedLength(minG);
            setPicks(Array(minG).fill(''));
        }
    }, [openDialog, pack]);

    const resizePicks = (newLen) => {
        setPredictedLength(newLen);
        setPicks(prev => {
            const next = Array(newLen).fill('');
            for (let i = 0; i < Math.min(prev.length, newLen); i++) next[i] = prev[i];
            return next;
        });
    };

    const setPickAt = (i, val) => {
        setPicks(prev => {
            const next = [...prev];
            next[i] = val;
            return next;
        });
    };

    const allPicked = useMemo(() => picks.length > 0 && picks.every(p => p), [picks]);

    const submit = async () => {
        if (!pack) return;
        if (!allPicked) {
            toast.error('Pick a winner for every game first');
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/series-pack/enter`, {
                pack_id: pack.id,
                predicted_length: predictedLength,
                picks,
            });
            toast.success(`Locked in — predicted ${predictedLength}-game series!`);
            setOpenDialog(false);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not lock in entry');
        }
        setSubmitting(false);
    };

    if (!pack) return null;

    const entry = Number(pack.entry_price || 5);
    const payouts = pack.payouts || {};
    const p4 = Number(payouts['4'] || 0);
    const p5 = Number(payouts['5'] || 0);
    const p6 = Number(payouts['6'] || 0);
    const jackpot = Number(payouts['jackpot'] || 500);
    const minG = Number(pack.min_games || 4);
    const maxG = Number(pack.max_games || 7);

    const hasEntered = !!myEntry;
    const isSettled = myEntry && ['won', 'lost', 'refund'].includes(myEntry.status);

    // Length options (min_games .. max_games)
    const lengthOptions = [];
    for (let n = minG; n <= maxG; n++) lengthOptions.push(n);

    return (
        <>
            <AnimatePresence>
                <motion.div
                    key={pack.id}
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    className="relative w-full overflow-hidden rounded-2xl border p-4 sm:p-5"
                    style={{
                        background: 'linear-gradient(135deg, rgba(255,140,0,0.12) 0%, rgba(255,59,128,0.10) 50%, rgba(124,58,237,0.12) 100%)',
                        borderColor: 'rgba(255,140,0,0.45)',
                    }}
                    data-testid="series-pack-banner"
                >
                    <motion.div
                        aria-hidden="true"
                        className="absolute inset-y-0 left-0 w-2"
                        style={{ background: 'linear-gradient(180deg, #FF8C00, #7C3AED)' }}
                        animate={{ opacity: [0.6, 1, 0.6] }}
                        transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
                    />

                    <div className="relative pl-3">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-[#FF8C00] bg-[#FF8C00]/15 border border-[#FF8C00]/40 px-2 py-0.5 rounded-full">
                                Series Pack · Playoffs
                            </span>
                            {countdown && !hasEntered && (
                                <span className="inline-flex items-center gap-1 text-[10px] text-[#A0AAB2]">
                                    <Clock className="w-3 h-3" />
                                    {countdown}
                                </span>
                            )}
                            {hasEntered && !isSettled && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#39FF14] bg-[#39FF14]/10 px-2 py-0.5 rounded-full">
                                    <CheckCircle2 className="w-3 h-3" /> Locked in · {myEntry.predicted_length}-game pick
                                </span>
                            )}
                            {isSettled && (
                                <span
                                    className={`inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 rounded-full ${myEntry.status === 'won' ? 'bg-[#39FF14]/20 text-[#39FF14]' : myEntry.status === 'refund' ? 'bg-[#FFD700]/20 text-[#FFD700]' : 'bg-[#FF3B30]/20 text-[#FF3B30]'}`}
                                >
                                    <Trophy className="w-3 h-3" />
                                    {myEntry.status === 'won'
                                        ? `Won $${Number(myEntry.payout_amount || 0).toFixed(2)} · ${myEntry.hits_count}/${myEntry.predicted_length}${myEntry.perfect ? ' · PERFECT' : ''}`
                                        : myEntry.status === 'refund'
                                            ? `Refunded $${Number(myEntry.payout_amount || 0).toFixed(2)}`
                                            : `${myEntry.hits_count}/${myEntry.predicted_length} hit`}
                                </span>
                            )}
                        </div>

                        <p className="text-white font-black text-base sm:text-lg leading-tight">
                            {pack.name} — ${entry.toFixed(0)} in → up to ${jackpot.toFixed(0)} jackpot
                        </p>
                        <p className="text-[#A0AAB2] text-xs sm:text-sm mt-0.5">
                            Pick how many games the series ends in <strong>and</strong> the winner of each. Nail it exactly = JACKPOT.
                        </p>

                        {/* Payout tiers */}
                        <div className="grid grid-cols-4 gap-2 mt-3">
                            {[
                                { label: '4 hits', amount: p4, color: '#A0AAB2', testId: 'series-tier-4' },
                                { label: '5 hits', amount: p5, color: '#FFD700', testId: 'series-tier-5' },
                                { label: '6 hits', amount: p6, color: '#FF8C00', testId: 'series-tier-6' },
                                { label: 'PERFECT', amount: jackpot, color: '#39FF14', testId: 'series-tier-jackpot' },
                            ].map(tier => (
                                <div
                                    key={tier.label}
                                    className="rounded-lg border bg-[#0a0a0a]/60 p-2 text-center"
                                    style={{ borderColor: `${tier.color}40` }}
                                    data-testid={tier.testId}
                                >
                                    <p className="text-[10px] text-[#A0AAB2] uppercase tracking-wider font-bold">
                                        {tier.label}
                                    </p>
                                    <p className="text-lg font-mono font-black" style={{ color: tier.color }}>
                                        ${tier.amount.toFixed(0)}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {/* Matchup */}
                        <div className="mt-3 flex items-center justify-center gap-3 rounded-lg bg-[#0a0a0a]/60 border border-white/10 py-2">
                            <span className="text-white font-bold text-sm">{pack.team_a}</span>
                            <span className="text-[#A0AAB2] text-xs font-mono">VS</span>
                            <span className="text-white font-bold text-sm">{pack.team_b}</span>
                        </div>

                        {/* Action */}
                        <div className="mt-4">
                            {!hasEntered ? (
                                <Button
                                    onClick={() => setOpenDialog(true)}
                                    className="w-full font-black text-base h-12"
                                    style={{ background: 'linear-gradient(135deg, #FF8C00, #7C3AED)', color: 'white' }}
                                    data-testid="series-pack-open-btn"
                                >
                                    <Sparkles className="w-5 h-5 mr-2" />
                                    Predict the Series · ${entry.toFixed(2)}
                                </Button>
                            ) : (
                                <div className="rounded-lg bg-[#0a0a0a]/60 border border-white/10 p-3">
                                    <p className="text-[10px] uppercase tracking-wider text-[#A0AAB2] mb-1 font-bold">
                                        Your prediction · {myEntry.predicted_length}-game series
                                    </p>
                                    <ul className="space-y-0.5">
                                        {(myEntry.picks || []).map((p, i) => {
                                            const game = (pack.games || [])[i] || {};
                                            const winner = game.winner;
                                            let dot = '#666';
                                            if (winner === 'void') dot = '#FFD700';
                                            else if (winner && winner === p) dot = '#39FF14';
                                            else if (winner) dot = '#FF3B30';
                                            return (
                                                <li key={i} className="flex items-center gap-2 text-xs">
                                                    <span
                                                        className="inline-block w-2 h-2 rounded-full shrink-0"
                                                        style={{ background: dot, boxShadow: winner ? `0 0 6px ${dot}` : 'none' }}
                                                    />
                                                    <span className="text-[#A0AAB2] font-mono w-12 shrink-0">Game {i + 1}:</span>
                                                    <span className="text-white font-semibold">{p}</span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                    {!isSettled && (
                                        <p className="text-center text-[10px] text-[#A0AAB2] mt-2">
                                            Entered {fmtCT(myEntry.entered_at)}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </motion.div>
            </AnimatePresence>

            {/* Pick Dialog */}
            <Dialog open={openDialog} onOpenChange={setOpenDialog}>
                <DialogContent className="bg-[#0e0e0e] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="series-pack-pick-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-white text-xl font-black flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-[#FF8C00]" />
                            {pack.name}
                        </DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            Step 1: pick how many games the series ends in.
                            Step 2: pick a winner for each game.
                            <br />
                            <strong className="text-[#39FF14]">Nail the exact length AND every winner = ${jackpot.toFixed(0)} jackpot.</strong>
                        </DialogDescription>
                    </DialogHeader>

                    {/* Length picker */}
                    <div>
                        <p className="text-[11px] uppercase tracking-widest text-[#A0AAB2] font-bold mb-2">
                            Series ends in...
                        </p>
                        <div className="grid grid-cols-4 gap-2">
                            {lengthOptions.map(n => (
                                <button
                                    key={n}
                                    type="button"
                                    onClick={() => resizePicks(n)}
                                    className={`rounded-lg border py-3 font-black text-sm transition ${predictedLength === n
                                        ? 'bg-[#FF8C00] border-[#FF8C00] text-black'
                                        : 'bg-[#0a0a0a] border-white/10 text-white hover:border-[#FF8C00]/60'
                                        }`}
                                    data-testid={`series-length-${n}`}
                                >
                                    {n} games
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Per-game winner pickers */}
                    <div className="mt-2">
                        <p className="text-[11px] uppercase tracking-widest text-[#A0AAB2] font-bold mb-2">
                            Winner of each game
                        </p>
                        <div className="space-y-2">
                            {picks.map((p, i) => {
                                const game = (pack.games || [])[i] || {};
                                return (
                                    <div key={i} className="rounded-lg border border-white/10 bg-[#0a0a0a] p-2">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-[10px] uppercase tracking-wider text-[#A0AAB2] font-bold">
                                                Game {i + 1}
                                            </span>
                                            {game.commence_time && (
                                                <span className="text-[10px] text-[#A0AAB2]">
                                                    {fmtCT(game.commence_time)}
                                                </span>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            {[pack.team_a, pack.team_b].map(team => (
                                                <button
                                                    key={team}
                                                    type="button"
                                                    onClick={() => setPickAt(i, team)}
                                                    className={`rounded-lg py-2 px-2 text-sm font-bold transition border ${p === team
                                                        ? 'bg-[#7C3AED] border-[#7C3AED] text-white'
                                                        : 'bg-[#0e0e0e] border-white/10 text-white hover:border-[#7C3AED]/60'
                                                        }`}
                                                    data-testid={`series-pick-${i}-${team.replace(/\s+/g, '-')}`}
                                                >
                                                    {team}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <DialogFooter className="flex-row gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setOpenDialog(false)}
                            className="flex-1 border-white/10 text-white hover:bg-white/5"
                            data-testid="series-pack-cancel-btn"
                        >
                            <X className="w-4 h-4 mr-1" /> Cancel
                        </Button>
                        <Button
                            onClick={submit}
                            disabled={submitting || !allPicked}
                            className="flex-1 font-black"
                            style={{ background: 'linear-gradient(135deg, #FF8C00, #7C3AED)', color: 'white' }}
                            data-testid="series-pack-confirm-btn"
                        >
                            {submitting ? 'Locking in…' : `Lock In · $${entry.toFixed(2)}`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default SeriesPackBanner;
